/**
 * API Client Instance
 * 
 * This module exports a singleton instance of the TurboClaimClient
 * configured for the frontend application with automatic token refresh
 * and subscription error handling.
 */

import TurboClaimClient from '@/api/client';
import { env } from '@/config/env';
import { auth } from '@/lib/firebase';
import { createTokenRefreshMiddleware } from '@/lib/token-refresh-middleware';
import { createSubscriptionErrorMiddleware } from '@/lib/subscription-error-middleware';
import { createRateLimitMiddleware, setToastFunction } from '@/lib/rate-limit-middleware';

// Store the current token
let currentToken: string | null = null;

// Store the navigation function (will be set by the app)
let navigationFunction: ((path: string) => void) | null = null;

// Get a fresh token for each request — Firebase SDK caches internally
// and only refreshes when the token is expired or about to expire
async function getTokenForRequest(): Promise<string> {
  if (auth.currentUser) {
    try {
      const token = await auth.currentUser.getIdToken();
      currentToken = token;
      return token;
    } catch {
      // Fall through to cached token
    }
  }

  if (!currentToken) {
    throw new Error('No authentication token available');
  }
  return currentToken;
}

// Helper to update the access token
export function setApiToken(token: string | (() => string | Promise<string>)) {
  if (typeof token === 'string') {
    currentToken = token;
  } else {
    // If a function is provided, we need to handle it differently
    // For now, just log a warning
    console.warn('[API Client] Function-based tokens not directly supported, use string tokens');
  }
}

// Helper to set navigation function for subscription error handling
export function setNavigationFunction(fn: (path: string) => void) {
  navigationFunction = fn;
}

// Helper to set toast function for rate limit notifications
export function setToastForRateLimiting(toastFn: (props: { title?: string; description?: string; variant?: 'default' | 'destructive' }) => void) {
  setToastFunction(toastFn);
}

// Create middleware that handles token refresh on 401 errors
const tokenRefreshMiddleware = createTokenRefreshMiddleware((token: string) => {
  currentToken = token;
});

// Create middleware that handles subscription errors
const subscriptionErrorMiddleware = createSubscriptionErrorMiddleware((path: string) => {
  if (navigationFunction) {
    navigationFunction(path);
  } else {
    console.warn('[API Client] Navigation function not set, cannot redirect');
  }
});

// Create middleware that handles rate limit errors (429)
const rateLimitMiddleware = createRateLimitMiddleware();

// Create a singleton client instance with function-based token provider and middleware
export const apiClient = new TurboClaimClient({
  basePath: env.apiBaseUrl,
  accessToken: getTokenForRequest,
  middleware: [tokenRefreshMiddleware, subscriptionErrorMiddleware, rateLimitMiddleware],
});

// Export the client as default
export default apiClient;
